import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { FinanceBalanceResponseDto } from '@/features/finances/types/finances-dto';
import { useAxiosAuth } from '@/hooks/useAxiosAuth.ts';
import { useFinancesPageFilter } from '@/features/finances/store/useFinancesPageFilter';
import { useCurrencyStore } from '@/store/useCurrencyStore.ts';
import { DateTimeHelper } from '@/utils/dateTimeHelper.ts';

export const useFinancesBalanceApi = () => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useFinancesPageFilter();
    const { currentCurrency } = useCurrencyStore();
    const { endDate, apartmentId } = filter;
    const apartmentIds = apartmentId?.map((apartment) => apartment.id);

    const {
        data: balanceData,
        isLoading: isLoadingBalanceData,
        isError: isErrorBalanceData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.finances.queryKeys.balance,
            apartmentIds,
            endDate,
            currentCurrency,
        ],
        queryFn: async (): Promise<FinanceBalanceResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.features.finances.endpoints.balance,
                {
                    apartmentId: apartmentIds,
                    endDate: DateTimeHelper.getDateOnly(endDate),
                },
            );
            const response = await axiosInstance.get<FinanceBalanceResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    return {
        balanceData,
        isLoadingBalanceData,
        isErrorBalanceData,
    };
};
